import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  FileText,
  Filter,
} from 'lucide-react';
import { useGastos, useDeleteGasto } from '../../hooks/useGastos';
import { useDebounce } from '../../hooks/useDebounce';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { formatCurrency, formatDate } from '../../lib/utils';
import { CATEGORIAS_GASTO, METODOS_PAGO } from '../../types/gasto';

const ITEMS_PER_PAGE = 20;

const getCategoriaInfo = (categoria: string) => {
  return CATEGORIAS_GASTO.find(c => c.value === categoria) || CATEGORIAS_GASTO[0];
};

const getMetodoPagoLabel = (metodo: string) => {
  return METODOS_PAGO.find(m => m.value === metodo)?.label || metodo;
};

export default function GastoList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: paginationData, isLoading, refetch } = useGastos({
    search: debouncedSearch.trim() || undefined,
    categoria: categoriaFiltro !== 'todas' ? categoriaFiltro : undefined,
    fechaInicio: fechaInicio || undefined,
    fechaFin: fechaFin || undefined,
    page,
    limit: ITEMS_PER_PAGE,
  });

  const deleteGasto = useDeleteGasto();

  const gastos = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCategoriaChange = (value: string) => {
    setCategoriaFiltro(value);
    setPage(1);
  };

  const handleFechaInicioChange = (value: string) => {
    setFechaInicio(value);
    setPage(1);
  };

  const handleFechaFinChange = (value: string) => {
    setFechaFin(value);
    setPage(1);
  };

  const handleDelete = async (id: number, numero: string) => {
    if (!confirm(`¿Estás seguro de eliminar el gasto ${numero}?`)) return;
    try {
      await deleteGasto.mutateAsync(id);
      refetch();
    } catch {
      // Error ya manejado en el hook
    }
  };

  const handleLimpiarFiltros = () => {
    setSearchTerm('');
    setCategoriaFiltro('todas');
    setFechaInicio('');
    setFechaFin('');
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando gastos...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gastos</h1>
          <p className="text-sm text-gray-500">
            {totalItems} gasto(s) registrados
          </p>
        </div>
        <Link to="/gastos/nuevo">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={18} />
            Nuevo Gasto
          </Button>
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por concepto, descripción o número..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2 min-w-[180px]">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            <select
              value={categoriaFiltro}
              onChange={(e) => handleCategoriaChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="todas">Todas las categorías</option>
              {CATEGORIAS_GASTO.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleLimpiarFiltros}
            className="text-gray-500"
          >
            <Filter size={16} className="mr-2" />
            Limpiar
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha desde</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => handleFechaInicioChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha hasta</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => handleFechaFinChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {gastos.map((gasto) => {
          const categoriaInfo = getCategoriaInfo(gasto.categoria);
          return (
            <div key={gasto.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: categoriaInfo?.color || '#6B7280' }}
                  />
                  <h3 className="font-semibold text-gray-800">{gasto.concepto}</h3>
                </div>
                <div className="flex gap-1">
                  <Link
                    to={`/gastos/${gasto.id}`}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Eye size={16} />
                  </Link>
                  <Link
                    to={`/gastos/editar/${gasto.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                  <button
                    onClick={() => handleDelete(gasto.id, gasto.numeroGasto)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Categoría</span>
                  <span className="font-medium text-gray-700">{categoriaInfo?.label || gasto.categoria}</span>
                </div>
                {gasto.descripcion && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Descripción</span>
                    <span className="text-gray-600 truncate max-w-[150px]">{gasto.descripcion}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto</span>
                  <span className="font-semibold text-red-600">{formatCurrency(gasto.monto)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha</span>
                  <span className="text-xs text-gray-400">{formatDate(gasto.fecha)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Método de Pago</span>
                  <span className="text-xs text-gray-500">{getMetodoPagoLabel(gasto.metodoPago)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {gastos.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron gastos</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay gastos que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/gastos/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Registrar el primer gasto
          </Link>
        </div>
      )}

      {totalItems > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}